import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, Table, Button, InputNumber, Checkbox, Row, Col, Typography, Divider, Card, Space, Upload, message, AutoComplete } from 'antd';
import { Plus, Trash, X, UploadCloud, Paperclip, ExternalLink, Building } from 'lucide-react';
import dayjs from 'dayjs';
import HSNSearchDropdown from './HSNSearchDropdown';
import CodeTypeSelect, { codeTypeFromIsServc, isServcFromCodeType } from './CodeTypeSelect';
import { formatIndianCurrency, numberToWords } from '../utils/numberToWords';
import { uploadPurchaseInvoiceFile, fetchVendors, fetchInvoiceCategories, fetchDepartments } from '../utils/api';
import { UOM_OPTIONS } from '../utils/uom';

const { Title, Text } = Typography;
const { Option } = Select;

function EditInvoiceModal({ invoice, visible, onClose, onSave, clients, config, currentUser }) {
    const [form] = Form.useForm();
    const [items, setItems] = useState([]);
    const [subtotal, setSubtotal] = useState(0);
    const [taxTotal, setTaxTotal] = useState(0);
    const [grandTotal, setGrandTotal] = useState(0);
    const [totalTds, setTotalTds] = useState(0);
    const [purchaseInvoiceFile, setPurchaseInvoiceFile] = useState(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [isAdhocClientInvoice, setIsAdhocClientInvoice] = useState(false);
    const [vendorsList, setVendorsList] = useState([]);
    const [selectedVendorId, setSelectedVendorId] = useState(null);
    const [invoiceCategories, setInvoiceCategories] = useState([]);
    const [departments, setDepartments] = useState([]);

    // Payment terms from config
    const paymentTerms = Array.isArray(config?.paymentTerms) ? config.paymentTerms : [];

    useEffect(() => {
        if (visible && invoice) {
            // Prepare initial items
            const initialItems = (invoice.items || []).map((item, index) => {

                const tax = Number(item.tax || item.taxPercent || 0);

                // Check if API provides specific rates
                let cgstRate = item.cgstRate !== undefined ? Number(item.cgstRate) : (item.cgst !== undefined ? Number(item.cgst) : tax / 2);
                let sgstRate = item.sgstRate !== undefined ? Number(item.sgstRate) : (item.sgst !== undefined ? Number(item.sgst) : tax / 2);

                // Fallback to config for Rent invoices if rates are 0
                if (invoice.invoiceType === 'rent') {
                    if (cgstRate === 0) cgstRate = Number(config?.cgstRate || 0);
                    if (sgstRate === 0) sgstRate = Number(config?.sgstRate || 0);
                }

                // Recalculate tax to sync with rates
                const effectiveTax = cgstRate + sgstRate;

                return {
                    key: index,
                    hsn: item.hsn || '',
                    description: item.description || '',
                    unitPrice: Number(item.unitPrice || item.rate || 0),
                    qty: Number(item.qty || 1),
                    uom: item.uom || '',
                    tax: effectiveTax,
                    cgstRate: cgstRate,
                    sgstRate: sgstRate,
                    amount: Number(item.amount || 0),
                    tdsPercent: Number(item.tdsPercent || item.tds || 0),
                    tdsAmount: Number(item.tdsAmount || 0),
                    IsServc: item.IsServc || 'N'
                };
            });

            setItems(initialItems);
            calculateTotals(initialItems);

            // Load vendors
            fetchVendors({ limit: 100 })
                .then(res => setVendorsList(res.vendors || res || []))
                .catch(err => console.error("Error loading vendors:", err));

            // Load categories
            fetchInvoiceCategories()
                .then(cats => setInvoiceCategories(Array.isArray(cats) ? cats : []))
                .catch(err => console.error("Error loading categories:", err));

            // Load departments
            fetchDepartments()
                .then(depts => setDepartments(Array.isArray(depts) ? depts : []))
                .catch(err => console.error("Error loading departments:", err));

            const isAdhoc = Boolean(
                invoice.isAdhocClientInvoice ||
                invoice.purchaseInvoiceNumber ||
                invoice.vendorName ||
                invoice.purchaseInvoiceUrl
            );
            setIsAdhocClientInvoice(isAdhoc);
            setSelectedVendorId(invoice.vendorId || null);

            setPurchaseInvoiceFile(
                invoice.purchaseInvoiceUrl ? {
                    url: invoice.purchaseInvoiceUrl,
                    originalName: invoice.purchaseInvoiceOriginalName || 'Attached Invoice'
                } : null
            );

            // Set form values
            form.setFieldsValue({
                clientId: typeof invoice.client === 'object' ? (invoice.client._id || invoice.client.id) : invoice.client,
                invoiceNumber: invoice.invoiceNumber,
                invoiceDate: invoice.invoiceDate ? dayjs(invoice.invoiceDate) : null,
                financialYear: invoice.financialYear,
                paymentTerm: invoice.paymentTerm, // Match existing string/id
                isAdhocClientInvoice: isAdhoc,
                invoiceCategoryId: invoice.invoiceCategoryId || '',
                category: invoice.category || '',
                department: invoice.department || '',
                vendorName: invoice.vendorName || '',
                vendorInvoiceDate: invoice.vendorInvoiceDate ? dayjs(invoice.vendorInvoiceDate) : null,
                vendorPoNumber: invoice.vendorPoNumber || '',
                vendorPoDate: invoice.vendorPoDate ? dayjs(invoice.vendorPoDate) : null,
                purchaseInvoiceNumber: invoice.purchaseInvoiceNumber || '',
                poNumber: invoice.poNumber || '',
                poDate: invoice.poDate ? dayjs(invoice.poDate) : null,
                showShipTo: invoice.showShipTo || 'no',
                placeOfSupply: invoice.placeOfSupply || '',
                items: initialItems
            });
        }
    }, [visible, invoice, form]);

    const handleFileUpload = async (file) => {
        if (file.size > 15 * 1024 * 1024) {
            message.error('File size must be less than 15MB');
            return false;
        }
        setUploadingFile(true);
        try {
            const res = await uploadPurchaseInvoiceFile(file);
            setPurchaseInvoiceFile({
                url: res.url,
                originalName: res.originalName || file.name
            });
            message.success('Purchase invoice uploaded successfully');
        } catch (err) {
            console.error('File upload error:', err);
            message.error(err.message || 'Failed to upload purchase invoice');
        } finally {
            setUploadingFile(false);
        }
        return false;
    };

    const calculateItemValues = (item) => {
        let netAmount = 0;
        let cgstRate = 0;
        let sgstRate = 0;

        if (invoice?.invoiceType === 'rent') {
            netAmount = Number(item.amount) || 0;
            cgstRate = Number(item.cgstRate) || 0;
            sgstRate = Number(item.sgstRate) || 0;
        } else {
            const qty = Number(item.qty) || 0;
            const unitPrice = Number(item.unitPrice) || 0;
            netAmount = qty * unitPrice;
            const taxRate = Number(item.tax) || 0;
            cgstRate = taxRate / 2;
            sgstRate = taxRate / 2;
        }

        const cgstAmount = netAmount * (cgstRate / 100);
        const sgstAmount = netAmount * (sgstRate / 100);
        const totalAmount = netAmount + cgstAmount + sgstAmount;
        const tdsPercent = invoice?.invoiceType === 'rent' ? 0 : (Number(item.tdsPercent ?? item.tds ?? 0) || 0);
        const tdsAmount = Math.round(netAmount * (tdsPercent / 100) * 100) / 100;

        return { netAmount, cgstAmount, sgstAmount, totalAmount, tdsPercent, tdsAmount };
    };

    const calculateTotals = (currentItems) => {
        let sub = 0;
        let tax = 0;
        let tds = 0;

        currentItems.forEach(item => {
            const { netAmount, cgstAmount, sgstAmount, tdsAmount } = calculateItemValues(item);
            sub += netAmount;
            tax += (cgstAmount + sgstAmount);
            tds += (tdsAmount || 0);
        });

        setSubtotal(sub);
        setTaxTotal(tax);
        setGrandTotal(sub + tax);
        setTotalTds(tds);
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: value };

        // Sync total tax for backend consistency if Rent
        if (invoice?.invoiceType === 'rent' && (field === 'cgstRate' || field === 'sgstRate')) {
            const cgst = field === 'cgstRate' ? value : newItems[index].cgstRate;
            const sgst = field === 'sgstRate' ? value : newItems[index].sgstRate;
            newItems[index].tax = Number(cgst || 0) + Number(sgst || 0);
        }

        setItems(newItems);
        calculateTotals(newItems);
    };

    const addItem = () => {
        const newItem = {
            key: Date.now(),
            description: '',
            hsn: '',
            unitPrice: 0,
            qty: 1,
            uom: '',
            tax: 0,
            cgstRate: 0,
            sgstRate: 0,
            tdsPercent: 0,
            tdsAmount: 0,
            amount: 0, // Base amount for rent
            IsServc: invoice?.invoiceType === 'rent' ? 'Y' : 'N'
        };
        const newItems = [...items, newItem];
        setItems(newItems);
        calculateTotals(newItems);
    };

    const removeItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
        calculateTotals(newItems);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();

            // HSN (goods) items require a UOM; SAC (service) items make it optional
            if (invoice?.invoiceType !== 'rent') {
                const missingUomIdx = items.findIndex(it => (it.IsServc || 'N') !== 'Y' && !it.uom);
                if (missingUomIdx !== -1) {
                    alert(`Row ${missingUomIdx + 1}: UOM is required for items with HSN Code.`);
                    return;
                }
            }

            // Re-calculate one last time to be sure
            let sub = 0;
            let tax = 0;
            let totalTdsAmount = 0;

            const processedItems = items.map(item => {
                const { netAmount, cgstAmount, sgstAmount, totalAmount, tdsPercent, tdsAmount } = calculateItemValues(item);
                const taxAmount = cgstAmount + sgstAmount;
                sub += netAmount;
                tax += taxAmount;
                totalTdsAmount += (tdsAmount || 0);

                return {
                    hsn: item.hsn,
                    description: item.description,
                    unitPrice: item.unitPrice,
                    qty: item.qty,
                    uom: item.uom,
                    tax: item.tax, // normalized to 'tax' for backend
                    cgstRate: item.cgstRate,
                    sgstRate: item.sgstRate,
                    netAmount: netAmount,
                    taxAmount: taxAmount,
                    tdsPercent: tdsPercent || 0,
                    tdsAmount: tdsAmount || 0,
                    totalAmount: totalAmount,
                    amount: invoice?.invoiceType === 'rent' ? netAmount : totalAmount,
                    IsServc: item.IsServc
                };
            });

            const grossTotal = sub + tax;
            const roundedTotal = Math.round(grossTotal);
            const roundOff = Math.round((roundedTotal - grossTotal) * 100) / 100;
            const total = roundedTotal;

            const updatedData = {
                ...values,
                client: values.clientId, // pass ID
                isAdhocClientInvoice: Boolean(isAdhocClientInvoice),
                invoiceCategoryId: values.invoiceCategoryId || values.category || null,
                category: values.category || (invoiceCategories.find(c => String(c.id) === String(values.invoiceCategoryId))?.name) || '',
                department: values.department ? String(values.department).trim() : '',
                vendorName: isAdhocClientInvoice && values.vendorName ? String(values.vendorName).trim() : '',
                vendorId: isAdhocClientInvoice ? (selectedVendorId || null) : null,
                vendorInvoiceDate: isAdhocClientInvoice && values.vendorInvoiceDate ? values.vendorInvoiceDate.format('YYYY-MM-DD') : null,
                vendorPoNumber: isAdhocClientInvoice && values.vendorPoNumber ? String(values.vendorPoNumber).trim() : '',
                vendorPoDate: isAdhocClientInvoice && values.vendorPoDate ? values.vendorPoDate.format('YYYY-MM-DD') : null,
                purchaseInvoiceNumber: isAdhocClientInvoice && values.purchaseInvoiceNumber ? String(values.purchaseInvoiceNumber).trim() : '',
                purchaseInvoiceUrl: isAdhocClientInvoice ? (purchaseInvoiceFile?.url || '') : '',
                purchaseInvoiceOriginalName: isAdhocClientInvoice ? (purchaseInvoiceFile?.originalName || '') : '',
                // Store dates as YYYY-MM-DD (date-only) strings to avoid timezone shifts between
                // browser (IST) and server (UTC) when rendering PDFs.
                invoiceDate: values.invoiceDate ? values.invoiceDate.format('YYYY-MM-DD') : null,
                poNumber: values.poNumber ? String(values.poNumber).trim() : '',
                poDate: values.poDate ? values.poDate.format('YYYY-MM-DD') : null,
                items: processedItems,
                totals: {
                    subtotal: sub,
                    cgst: tax / 2,
                    sgst: tax / 2,
                    roundOff,
                    total,
                    totalTds: totalTdsAmount || 0,
                    netPayable: total - (totalTdsAmount || 0)
                },
                amountInWords: numberToWords(total),
                invoiceNumber: values.invoiceNumber,
                creator: invoice.creator
            };

            onSave(updatedData);
        } catch (error) {
            console.error("Validation failed:", error);
        }
    };

    const getColumns = () => {
        const commonColumns = [
            {
                title: '#',
                key: 'index',
                width: 50,
                render: (_, __, index) => index + 1
            },
            {
                title: 'HSN/SAC',
                dataIndex: 'hsn',
                width: 120,
                render: (text, record, index) => (
                    <HSNSearchDropdown
                        value={text}
                        onChange={(val) => handleItemChange(index, 'hsn', val)}
                        inputClassName="ant-input-sm"
                        style={{ width: '100%', verticalAlign: 'middle' }}
                    />
                )
            },
            {
                title: 'Description',
                dataIndex: 'description',
                render: (text, record, index) => (
                    <Input.TextArea
                        value={text}
                        onChange={e => handleItemChange(index, 'description', e.target.value)}
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        style={{ fontSize: '12px' }}
                    />
                )
            }
        ];

        if (invoice?.invoiceType === 'rent') {
            return [
                ...commonColumns,
                {
                    title: 'Amount',
                    dataIndex: 'amount',
                    width: 150,
                    render: (val, record, index) => (
                        <InputNumber
                            min={0}
                            value={val}
                            onChange={v => handleItemChange(index, 'amount', v)}
                            style={{ width: '100%' }}
                            formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/₹\s?|(,*)/g, '')}
                        />
                    )
                },
                {
                    title: 'CGST %',
                    dataIndex: 'cgstRate',
                    width: 80,
                    render: (val, record, index) => (
                        <InputNumber min={0} max={100} value={val} onChange={v => handleItemChange(index, 'cgstRate', v)} style={{ width: '100%' }} />
                    )
                },
                {
                    title: 'CGST Amt',
                    key: 'cgstAmount',
                    width: 100,
                    align: 'right',
                    render: (_, record) => {
                        const { cgstAmount } = calculateItemValues(record);
                        return <Text>{formatIndianCurrency(cgstAmount)}</Text>;
                    }
                },
                {
                    title: 'SGST %',
                    dataIndex: 'sgstRate',
                    width: 80,
                    render: (val, record, index) => (
                        <InputNumber min={0} max={100} value={val} onChange={v => handleItemChange(index, 'sgstRate', v)} style={{ width: '100%' }} />
                    )
                },
                {
                    title: 'SGST Amt',
                    key: 'sgstAmount',
                    width: 100,
                    align: 'right',
                    render: (_, record) => {
                        const { sgstAmount } = calculateItemValues(record);
                        return <Text>{formatIndianCurrency(sgstAmount)}</Text>;
                    }
                },
                {
                    title: 'Total',
                    key: 'total',
                    width: 150,
                    align: 'right',
                    render: (_, record) => {
                        const { totalAmount } = calculateItemValues(record);
                        return <Text strong>{formatIndianCurrency(totalAmount)}</Text>;
                    }
                },
                {
                    title: '',
                    key: 'action',
                    width: 50,
                    render: (_, __, index) => (
                        <Button
                            type="text"
                            danger
                            icon={<Trash size={16} />}
                            onClick={() => removeItem(index)}
                        />
                    )
                }
            ];
        }

        // Default / Adhoc Columns
        return [
            ...commonColumns,
            {
                title: 'Qty',
                dataIndex: 'qty',
                width: 80,
                render: (val, record, index) => (
                    <InputNumber min={0} value={val} onChange={v => handleItemChange(index, 'qty', v)} style={{ width: '100%' }} />
                )
            },
            {
                title: 'UOM',
                dataIndex: 'uom',
                width: 110,
                render: (val, record, index) => (
                    <Select
                        value={val}
                        onChange={v => handleItemChange(index, 'uom', v)}
                        style={{ width: '100%' }}
                        options={UOM_OPTIONS}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                    />
                )
            },
            {
                title: 'Unit Price',
                dataIndex: 'unitPrice',
                width: 130,
                render: (val, record, index) => (
                    <InputNumber
                        min={0}
                        value={val}
                        onChange={v => handleItemChange(index, 'unitPrice', v)}
                        style={{ width: '100%' }}
                        formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    />
                )
            },
            {
                title: 'Tax %',
                dataIndex: 'tax',
                width: 80,
                render: (val, record, index) => (
                    <InputNumber min={0} max={100} value={val} onChange={v => handleItemChange(index, 'tax', v)} style={{ width: '100%' }} />
                )
            },
            {
                title: 'TDS %',
                dataIndex: 'tdsPercent',
                width: 95,
                render: (val, record, index) => (
                    <Select
                        value={val !== undefined && val !== null && val !== '' ? String(val) : '0'}
                        onChange={v => handleItemChange(index, 'tdsPercent', Number(v))}
                        style={{ width: '100%' }}
                        options={[
                            { label: '0%', value: '0' },
                            { label: '0.1%', value: '0.1' },
                            { label: '1%', value: '1' },
                            { label: '2%', value: '2' },
                            { label: '5%', value: '5' },
                            { label: '10%', value: '10' }
                        ]}
                    />
                )
            },
            {
                title: 'TDS Amt',
                key: 'tdsAmount',
                width: 100,
                align: 'right',
                render: (_, record) => {
                    const { tdsAmount } = calculateItemValues(record);
                    return <Text style={{ color: tdsAmount > 0 ? '#dc2626' : undefined }}>{formatIndianCurrency(tdsAmount)}</Text>;
                }
            },
            {
                title: 'Amount',
                key: 'amount',
                width: 120,
                align: 'right',
                render: (_, record) => {
                    const { totalAmount } = calculateItemValues(record);
                    return <Text strong>{formatIndianCurrency(totalAmount)}</Text>;
                }
            },
            {
                title: 'Code Type',
                dataIndex: 'IsServc',
                width: 110,
                align: 'center',
                render: (val, record, index) => (
                    <CodeTypeSelect
                        size="small"
                        value={codeTypeFromIsServc(val)}
                        onChange={v => handleItemChange(index, 'IsServc', isServcFromCodeType(v))}
                    />
                )
            },
            {
                title: '',
                key: 'action',
                width: 50,
                render: (_, __, index) => (
                    <Button
                        type="text"
                        danger
                        icon={<Trash size={16} />}
                        onClick={() => removeItem(index)}
                    />
                )
            }
        ];
    };

    const columns = getColumns();

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            onOk={handleOk}
            width={1100}
            title={<Title level={4} style={{ margin: 0 }}>Edit Invoice #{invoice?.invoiceNumber}</Title>}
            okText="Save Changes"
            cancelText="Cancel"
            maskClosable={false}
            className="edit-invoice-modal"
            styles={{ body: { padding: '24px 0' } }}
        >
            <Form form={form} layout="vertical">
                <div style={{ padding: '0 24px' }}>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Client" name="clientId">
                                <Select
                                    placeholder="Select Client"
                                    disabled={false} // Requirement: Editable all the time
                                    options={clients.map(c => ({ label: c.name || c.registeredName, value: c._id }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Invoice No." name="invoiceNumber">
                                <Input disabled={false} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Invoice Date" name="invoiceDate">
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD MMM YYYY"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Financial Year" name="financialYear">
                                <Input disabled={false} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Payment Terms" name="paymentTerm">
                                <Select placeholder="Select Payment Term">
                                    {paymentTerms.map(term => (
                                        <Option key={term.id} value={term.label}>{term.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Ship To Required" name="showShipTo">
                                <Select>
                                    <Option value="yes">Yes</Option>
                                    <Option value="no">No</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Mode Toggle: Adhoc Client Invoice */}
                    <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Form.Item name="isAdhocClientInvoice" valuePropName="checked" style={{ marginBottom: 0 }}>
                            <Checkbox
                                checked={isAdhocClientInvoice}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setIsAdhocClientInvoice(checked);
                                    form.setFieldsValue({ isAdhocClientInvoice: checked });
                                }}
                            >
                                <span style={{ fontWeight: 600, fontSize: '13px', color: '#1d4ed8' }}>
                                    Adhoc Client Invoice
                                </span>
                            </Checkbox>
                        </Form.Item>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {isAdhocClientInvoice
                                ? 'Vendor & procurement fields enabled (internal tracking only, hidden from client PDF)'
                                : 'Check to record vendor purchase invoice and procurement tracking'}
                        </Text>
                    </div>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Category" name="invoiceCategoryId">
                                <Select
                                    placeholder="Select Category"
                                    options={invoiceCategories.map(cat => ({ label: cat.name, value: cat.id }))}
                                    showSearch
                                    optionFilterProp="label"
                                    allowClear
                                    onChange={(val) => {
                                        const cat = invoiceCategories.find(c => String(c.id) === String(val));
                                        form.setFieldsValue({ invoiceCategoryId: val, category: cat?.name || '' });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="PO Number" name="poNumber">
                                <Input placeholder="PO Number" allowClear />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="PO Date" name="poDate">
                                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {isAdhocClientInvoice && (
                        <div style={{
                            marginTop: 4,
                            marginBottom: 16,
                            padding: '16px',
                            background: '#f8fafc',
                            borderRadius: 8,
                            border: '1px solid #cbd5e1'
                        }}>
                            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: 14, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Building size={16} color="#2563eb" />
                                <span>Vendor, PO & Department Details (Internal Tracking - Hidden from Client)</span>
                            </div>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item label="Department" name="department">
                                        <Select
                                            placeholder="Select Department"
                                            options={departments.map(dept => ({ label: dept.name, value: dept.name }))}
                                            showSearch
                                            optionFilterProp="label"
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Vendor Name" name="vendorName">
                                        <AutoComplete
                                            options={vendorsList.map(v => ({ value: v.name }))}
                                            placeholder="Select or type vendor name"
                                            filterOption={(inputValue, option) =>
                                                (option?.value ?? '').toUpperCase().indexOf((inputValue || '').toUpperCase()) !== -1
                                            }
                                            onChange={(val) => {
                                                form.setFieldsValue({ vendorName: val || '' });
                                                const found = vendorsList.find(v => (v.name || '').toLowerCase() === (val || '').toLowerCase());
                                                setSelectedVendorId(found ? found._id : null);
                                            }}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Vendor Invoice No." name="purchaseInvoiceNumber">
                                        <Input placeholder="Enter vendor invoice number" allowClear />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item label="Vendor Invoice Date" name="vendorInvoiceDate">
                                        <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" placeholder="Select Vendor Invoice Date" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Vendor PO Number" name="vendorPoNumber">
                                        <Input placeholder="Enter Vendor PO Number" allowClear />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Vendor PO Date" name="vendorPoDate">
                                        <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" placeholder="Select Vendor PO Date" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>
                                    <div style={{ marginBottom: 6 }}>
                                        <Text strong style={{ fontSize: '13px' }}>Attach Vendor Invoice (Optional)</Text>
                                    </div>
                                    {purchaseInvoiceFile ? (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '8px 12px',
                                            background: '#f0fdf4',
                                            border: '1px solid #bbf7d0',
                                            borderRadius: 6,
                                            maxWidth: 450
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                                                <Paperclip size={16} color="#16a34a" />
                                                <span style={{ fontSize: '13px', fontWeight: 500, color: '#15803d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                                                    {purchaseInvoiceFile.originalName || 'Attached Invoice'}
                                                </span>
                                                {purchaseInvoiceFile.url && (
                                                    <a
                                                        href={purchaseInvoiceFile.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: 2, marginLeft: 4 }}
                                                    >
                                                        <ExternalLink size={12} /> View
                                                    </a>
                                                )}
                                            </div>
                                            <Button
                                                type="text"
                                                size="small"
                                                danger
                                                icon={<Trash size={14} />}
                                                onClick={() => setPurchaseInvoiceFile(null)}
                                                title="Remove attached file"
                                            />
                                        </div>
                                    ) : (
                                        <Upload
                                            beforeUpload={handleFileUpload}
                                            showUploadList={false}
                                            accept=".pdf,image/*"
                                            multiple={false}
                                        >
                                            <Button icon={<UploadCloud size={16} />} loading={uploadingFile}>
                                                {uploadingFile ? 'Uploading...' : 'Upload Vendor Invoice (PDF / Image)'}
                                            </Button>
                                        </Upload>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    )}
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <div style={{ padding: '0 24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <Title level={5} style={{ margin: 0 }}>Invoice Items</Title>
                        <Button type="primary" icon={<Plus size={16} />} onClick={addItem}>
                            Add Item
                        </Button>
                    </div>

                    <Table
                        dataSource={items}
                        columns={columns}
                        pagination={false}
                        size="small"
                        bordered
                        summary={() => {
                            const grossTotal = subtotal + taxTotal;
                            const roundedTotal = Math.round(grossTotal);
                            const roundOff = Math.round((roundedTotal - grossTotal) * 100) / 100;
                            return (
                                <Table.Summary fixed>
                                    <Table.Summary.Row style={{ background: '#fafafa' }}>
                                        <Table.Summary.Cell index={0} colSpan={columns.length - 2} className="text-right">
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                                                <Text type="secondary">Subtotal:</Text>
                                                <Text type="secondary">Tax Total:</Text>
                                                {roundOff !== 0 && <Text type="secondary">Round Off:</Text>}
                                                {totalTds > 0 && <Text type="secondary" style={{ color: '#dc2626' }}>Less: TDS Deducted:</Text>}
                                                <Text strong style={{ fontSize: 16 }}>Grand Total:</Text>
                                                {totalTds > 0 && <Text strong style={{ fontSize: 15, color: '#059669' }}>Net Receivable:</Text>}
                                            </div>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} colSpan={2} className="text-right">
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                                                <Text>{formatIndianCurrency(subtotal)}</Text>
                                                <Text>{formatIndianCurrency(taxTotal)}</Text>
                                                {roundOff !== 0 && <Text>{formatIndianCurrency(roundOff)}</Text>}
                                                {totalTds > 0 && <Text style={{ color: '#dc2626' }}>- {formatIndianCurrency(totalTds)}</Text>}
                                                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>{formatIndianCurrency(roundedTotal)}</Text>
                                                {totalTds > 0 && <Text strong style={{ fontSize: 15, color: '#059669' }}>{formatIndianCurrency(roundedTotal - totalTds)}</Text>}
                                            </div>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            );
                        }}
                    />
                </div>
            </Form>
        </Modal>
    );
}

export default EditInvoiceModal;
